import type { JSX } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Plus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCreateSuperAdmin } from '../hooks/useCreateSuperAdmin';
import { SuperAdminFormFields } from './SuperAdminFormFields';
import {
  createSuperAdminSchema,
  type CreateSuperAdminFormData,
} from '../schemas/super-admin.schemas';

export function CreateSuperAdminDialog(): JSX.Element {
  const [open, setOpen] = useState(false);
  const { createSuperAdmin, isLoading } = useCreateSuperAdmin();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateSuperAdminFormData>({
    resolver: zodResolver(createSuperAdminSchema),
  });

  const onSubmit = (data: CreateSuperAdminFormData): void => {
    createSuperAdmin(data, {
      onSuccess: () => {
        toast.success('Super admin created successfully');
        reset();
        setOpen(false);
      },
      onError: () => toast.error('Failed to create super admin'),
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Add Super Admin
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]" data-row-click-ignore>
        <DialogHeader>
          <DialogTitle>Create Super Admin</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <SuperAdminFormFields register={register} errors={errors} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
